<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog
      as="div"
      class="relative z-70"
      :modal="true"
      :initial-focus="focusTarget"
      @close="emits('close')"
    >
      <TransitionChild
        enter="duration-200 ease-fast-effect transition-opacity"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-spatial transition-opacity"
        leave-from="opacity-100"
        leave-to="opacity-0"
        as="template"
      >
        <BaseScrim class="z-60" />
      </TransitionChild>

      <div class="fixed z-70 flex flex-col items-center justify-end" :style="viewportStyle">
        <TransitionChild
          as="template"
          enter="duration-600 ease-fast-effect"
          enter-from="translate-y-full"
          enter-to="translate-y-0"
          leave="duration-200 ease-fast-effect"
          leave-from="translate-y-0"
          leave-to="translate-y-full"
        >
          <DialogPanel
            class="flex min-h-0 w-full max-w-lg flex-col justify-end outline-none"
            as="div"
          >
            <div
              class="bg-surface-container-low scroll-track-surface-container-low rounded-t-extra-large ease-fast-effect relative flex max-h-[95%] min-h-0 w-full flex-col overflow-hidden"
              :class="[
                attrs.class,
                isDragging ? 'transition-none!' : 'transition-transform duration-200',
              ]"
              :style="dragStyle"
            >
              <div ref="focusTarget" tabindex="-1" class="flex min-h-0 flex-col outline-none">
                <div
                  v-if="showDragHandle"
                  class="flex h-8 shrink-0 cursor-grab touch-none items-center justify-center active:cursor-grabbing"
                  @pointerdown="onDragStart"
                >
                  <div class="bg-on-surface-variant/40 h-1 w-8 rounded-full" aria-hidden="true" />
                </div>

                <DialogTitle
                  v-if="title"
                  as="h2"
                  class="text-headline-small text-on-surface shrink-0 px-6"
                  :class="[showDragHandle ? 'pb-4' : 'pt-6 pb-4']"
                >
                  {{ title }}
                </DialogTitle>

                <div
                  v-if="slots.header"
                  class="shrink-0 px-6"
                  :class="[!title && !showDragHandle && 'pt-6']"
                >
                  <slot name="header" />
                </div>

                <div
                  v-if="hasContent"
                  ref="content"
                  class="min-h-0 overflow-y-auto px-6"
                  :class="[
                    !hasActions && 'pb-6',
                    !title && !slots.header && !showDragHandle && 'pt-6',
                  ]"
                >
                  <slot />
                </div>

                <div
                  v-if="hasActions"
                  class="border-outline-variant bg-surface-container-low sticky bottom-0 flex shrink-0 flex-row justify-end gap-x-1 px-6 pt-4 pb-6"
                  :class="[!hasScrolledToTheEnd && 'border-t']"
                >
                  <slot name="actions" />
                </div>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script setup lang="ts">
  import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionRoot,
    TransitionChild,
  } from '@headlessui/vue'
  import { computed, nextTick, ref, toRef, useAttrs, useTemplateRef, watch } from 'vue'
  import { useScroll } from '@vueuse/core'
  import { useVisualViewport } from '@/core/composables/use-visual-viewport'
  import BaseScrim from '../dialog/BaseScrim.vue'

  interface Props {
    isOpen: boolean
    title?: string
    showDragHandle?: boolean
    /** Distance in px the sheet must be dragged down before it dismisses */
    dismissThreshold?: number
  }

  const props = withDefaults(defineProps<Props>(), {
    showDragHandle: true,
    dismissThreshold: 80,
  })

  const emits = defineEmits<{
    close: []
  }>()

  const slots = defineSlots<{
    default?: () => unknown
    header?: () => unknown
    actions?: () => unknown
  }>()

  defineOptions({ inheritAttrs: false })
  const attrs = useAttrs()

  const hasContent = computed(() => !!slots.default)
  const hasActions = computed(() => !!slots.actions)

  const focusTarget = useTemplateRef<HTMLElement>('focusTarget')
  const content = useTemplateRef<HTMLDivElement>('content')
  const { arrivedState } = useScroll(content)
  const hasScrolledToTheEnd = computed(() => {
    if (content.value) {
      return arrivedState.bottom || content.value.scrollHeight <= content.value.clientHeight
    }
    return true
  })

  const isOpen = toRef(props, 'isOpen')
  const visualViewport = useVisualViewport(isOpen, { overlayKeyboard: true })

  // Content-sized sheet, pinned flush above the keyboard via bottom padding.
  // Scrim above the sheet stays visible for dismiss.
  const viewportStyle = computed(() => ({
    inset: '0px',
    paddingBottom: `${visualViewport.keyboardOffset}px`,
  }))

  const scrollFocusedFieldIntoView = () => {
    const container = content.value
    const active = document.activeElement
    if (!container || !(active instanceof HTMLElement) || !container.contains(active)) return

    const containerRect = container.getBoundingClientRect()
    const activeRect = active.getBoundingClientRect()
    const padding = 12

    if (activeRect.bottom > containerRect.bottom) {
      container.scrollTop += activeRect.bottom - containerRect.bottom + padding
    } else if (activeRect.top < containerRect.top) {
      container.scrollTop -= containerRect.top - activeRect.top + padding
    }
  }

  watch(
    () => [visualViewport.keyboardOffset, visualViewport.height] as const,
    async () => {
      if (!props.isOpen) return
      await nextTick()
      scrollFocusedFieldIntoView()
    },
  )

  // Blur the trigger before Headless UI marks #app aria-hidden, avoiding the
  // "descendant retained focus" warning when the sheet has no other focusables yet.
  watch(
    () => props.isOpen,
    (open) => {
      if (open && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      if (!open) {
        resetDrag()
      }
    },
    { flush: 'sync' },
  )

  const dragOffset = ref(0)
  const isDragging = ref(false)
  let dragStartY = 0
  let activePointerId: number | null = null

  const dragStyle = computed(() => ({
    transform: `translateY(${dragOffset.value}px)`,
  }))

  const resetDrag = () => {
    isDragging.value = false
    dragOffset.value = 0
    dragStartY = 0
    activePointerId = null
  }

  const onDragStart = (event: PointerEvent) => {
    if (event.button !== 0) return

    isDragging.value = true
    dragStartY = event.clientY
    dragOffset.value = 0
    activePointerId = event.pointerId

    const target = event.currentTarget as HTMLElement
    target.setPointerCapture(event.pointerId)

    const onMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== activePointerId) return
      dragOffset.value = Math.max(0, moveEvent.clientY - dragStartY)
    }

    const onEnd = (endEvent: PointerEvent) => {
      if (endEvent.pointerId !== activePointerId) return

      target.releasePointerCapture(endEvent.pointerId)
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onEnd)
      target.removeEventListener('pointercancel', onEnd)

      const shouldDismiss = dragOffset.value >= props.dismissThreshold
      isDragging.value = false
      activePointerId = null

      if (shouldDismiss) {
        dragOffset.value = 0
        emits('close')
        return
      }

      dragOffset.value = 0
    }

    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onEnd)
    target.addEventListener('pointercancel', onEnd)
  }
</script>
